/**
 * Le videochiamate: le forme che leggono il server e il Cruscotto (e domani l'app). Il motore e'
 * LiveKit, in casa; il server HR decide chi entra e firma il gettone, il motore trasporta l'audio
 * e il video e basta. Il disegno e le decisioni di Cosimo stanno in `spec/videochiamate.md` e in
 * `spec/da-fare.md` §5 videochiamate (2026-10-26): gli account di sala gestiscono tutte le
 * chiamate **tranne le riservate**, **nessuna registrazione**, gli ospiti esterni passano
 * **sempre** dalla sala d'attesa.
 *
 * Le rotte, tutte sotto `/api/videochiamate`:
 *
 *   personale (gettone dell'app, `type: 'access'`)
 *     GET    /                          → ChiamataInElenco[]       le mie: in corso, programmate, finite
 *     POST   /                          ← NuovaChiamata  → ChiamataVista
 *     POST   /da-riunione/:eventId      ← { riservata?: boolean } → ChiamataVista   la crea o restituisce quella che c'e'
 *     GET    /:codice                   → ChiamataVista
 *     POST   /:codice/ingresso          → IngressoChiamata         il gettone LiveKit
 *     POST   /:codice/ospiti            ← NuovoOspite    → OspiteVista (manda l'invito)
 *     DELETE /:codice/ospiti/:id                                   revoca il link
 *
 *   gestione (moderatore: chi convoca, chi e' promosso, un account di sala ammesso)
 *     GET    /:codice/attesa            → AttesaVista[]            la sala d'attesa
 *     POST   /:codice/attesa/:id/ammetti
 *     POST   /:codice/attesa/:id/respingi
 *     POST   /:codice/partecipanti/:identita/silenzia   ← { traccia: 'audio' | 'video' }
 *     POST   /:codice/partecipanti/:identita/allontana
 *     POST   /:codice/partecipanti/:identita/promuovi
 *     POST   /:codice/partecipanti/:identita/riammetti   chi era stato allontanato puo' rientrare
 *     POST   /:codice/chiudi-ingressi   ← { chiusi: boolean }
 *     POST   /:codice/termina                                      chiude per tutti
 *
 *   account di sala (gettone `type: 'sala'`, mai valido sulle altre rotte)
 *     POST   /sala/accesso              ← AccessoSala    → SessioneSala   (la sala e' quella dell'account)
 *     POST   /sala/postazione           ← ScegliPostazione → SessioneSala (solo la sala dell'account: resta per le console di ieri)
 *     GET    /sala/me                   → SalaVista
 *     GET    /sala/chiamate             → ChiamataInElenco[]       di oggi, delle riunioni prenotate in questa sala
 *     ... e le stesse rotte /:codice/* di sopra, col gettone di sala
 *
 *   ospite esterno (pubbliche, il segreto e' nel link)
 *     GET    /ospite/:segreto           → InvitoOspite
 *     POST   /ospite/:segreto/attesa    ← { nome: string } → StatoAttesa
 *     GET    /ospite/:segreto/attesa    → StatoAttesa              si interroga ogni 2 s
 */
export const VIDEOCHIAMATE = {
    /** Il codice della stanza: lettere minuscole e cifre, non indovinabile. Va nell'indirizzo. */
    formaCodice: /^[a-z0-9]{12}$/,
    titolo: { min: 1, max: 120 },
    nome: { min: 1, max: 80 },
    /** Ogni quanti millisecondi l'ospite in attesa richiede se e' stato ammesso. */
    attesaIntervalloMs: 2000,
    /**
     * Un ospite che non interroga da tanto esce dalla sala d'attesa da solo. Era 60: una scheda
     * nascosta (l'ospite che nell'attesa guarda la posta) Chrome la sveglia una volta al minuto, e
     * a 60 l'ospite compariva e spariva dall'elenco e «Ammetti» rispondeva che non c'era piu'.
     */
    attesaScadenzaSecondi: 150,
    /**
     * Un ospite ammesso che cade e torna entro tanti secondi rientra da solo; dopo, o se nella stanza
     * non c'e' piu', torna in sala d'attesa e lo riammette un moderatore. Senza, «ammesso» valeva
     * per sempre: anche per chi riceveva il link inoltrato a riunione finita da un'ora.
     */
    ammissioneRientroSecondi: 120,
    /** Un ospite respinto non ribussa prima di tanti secondi: ogni bussata suona ai moderatori. */
    respintoPausaSecondi: 60,
    /** Una chiamata «adesso» (senza orario): il link dell'ospite vale tante ore dall'apertura. */
    adessoDurataOre: 12,
    /** Il link dell'ospite apre da tanti minuti prima dell'inizio programmato... */
    ospiteAnticipoMinuti: 15,
    /** ...e resta buono tanti minuti dopo la fine programmata. */
    ospiteMargineMinuti: 120,
    /**
     * Quanto vive il gettone LiveKit. Corto di proposito: un gettone non si ritira, e con sei ore
     * (la prima stesura) chi era stato allontanato, o l'ospite col link revocato, rientrava col
     * gettone che aveva in tasca. A chi resta collegato il motore lo rinnova da se'; chi cade per
     * piu' di tanto ne chiede uno nuovo a `/ingresso`, che rifa' i controlli. Era 15 minuti: tre
     * bastano a collegarsi, e accorciano la finestra in cui un allontanato rientra col gettone che
     * aveva (2026-10-27).
     */
    gettoneSecondi: 3 * 60,
};
/**
 * Le reazioni rapide (2026-10-28, scelte da Cosimo fra i miglioramenti): un gesto che dice
 * «d'accordo», «bravo», «che ridere» senza prendere la parola. Viaggiano sul canale dati del motore
 * (argomento `bestehr.reazione`, `{ r: <chiave> }`), come la mano alzata; non si scrivono da
 * nessuna parte e durano qualche secondo sullo schermo di tutti.
 *
 * Sul filo va la **chiave**, non il disegno: chi riceve accetta solo una chiave di questo elenco,
 * cosi' nessuno fa volare sullo schermo degli altri un testo a piacere. Il cuore e' blu (il Blu
 * Klein del marchio) e non rosso, anche perche' il cuore rosso si scrive con un selettore di
 * variante invisibile, che i cancelli del deposito non lasciano passare. I disegni sono scritti in
 * codice (`\u{...}`) perche' si leggano uguali in ogni editor.
 */
export const REAZIONI = [
    { chiave: 'pollice', disegno: '\u{1F44D}' },
    { chiave: 'applauso', disegno: '\u{1F44F}' },
    { chiave: 'cuore', disegno: '\u{1F499}' },
    { chiave: 'risata', disegno: '\u{1F602}' },
    { chiave: 'stupore', disegno: '\u{1F62E}' },
    { chiave: 'festa', disegno: '\u{1F389}' },
];
/** La chiave arrivata dal filo, se e' una delle nostre. */
export function chiaveReazione(x) {
    return REAZIONI.some((r) => r.chiave === x) ? x : null;
}
/** Il disegno di una chiave. */
export function disegnoReazione(chiave) {
    return REAZIONI.find((r) => r.chiave === chiave)?.disegno ?? '';
}
/** Quanto resta a schermo una reazione, e ogni quanto la stessa persona ne fa volare un'altra. */
export const REAZIONE_DURATA_MS = 4000;
export const REAZIONE_PAUSA_MS = 300;
/** Chi e' una persona dentro la stanza. Va nei metadati LiveKit del partecipante. */
export const RUOLI_CHIAMATA = ['interno', 'sala', 'ospite'];
/**
 * L'identita' LiveKit: `u:<userId>`, `g:<ospiteId>`, e per le sale `s:<accountId>.<sessione>`. E'
 * la chiave con cui il moderatore indica una persona nelle rotte `/partecipanti/:identita/*`.
 *
 * La sala porta la **sessione** (8 caratteri, nuova a ogni accesso): l'account e' uno per sede e
 * Cosimo vuole il multilogin (2026-10-26: *«possano fare anche multilogin per gestire piu call
 * contemporanee con il solito account»*). Il motore tiene **una** connessione per identita', e due
 * sale di COLLE nella stessa chiamata con la stessa identita' si butterebbero fuori a vicenda.
 */
export const FORMA_IDENTITA = /^(?:u:\d+|g:\d+(?:\.[a-z0-9]{8})?|s:\d+\.[a-z0-9]{8})$/;
/**
 * L'apparecchio dell'ospite (2026-11-03): 8 caratteri `[a-z0-9]` che la pagina ospite sceglie a caso e
 * tiene per la sua scheda, e che manda in sala d'attesa. L'ospite nella stanza e' `g:<ospiteId>.<apparecchio>`.
 *
 * Perche': lo stesso link aperto da due posti (la saletta di Bari e un cliente a cui l'avevano girato,
 * chiamata 44 del 3/11) era **una** identita' `g:8`, e il motore, che tiene una connessione per
 * identita', li buttava fuori a vicenda (`DUPLICATE_IDENTITY`, tre volte in un minuto, finche' la saletta
 * e' rimasta fuori). Adesso ogni apparecchio bussa per conto suo, il moderatore lo vede come una persona
 * a se', e dentro stanno tutti. `g:<ospiteId>` nudo resta valido per le pagine aperte prima.
 */
export const FORMA_APPARECCHIO = /^[a-z0-9]{8}$/;
export const STATI_CHIAMATA = ['programmata', 'in_corso', 'finita'];
/**
 * L'app «Beste Sale» per Windows e Mac (2026-10-29). L'installatore si scarica da `/sala` **solo**
 * con l'account di una sala: lo scaricamento porta un codice monouso, e l'app lo scambia con una
 * sessione di quell'account al primo avvio, cosi' entra da sola.
 *
 *     POST /sala/installatore   (gettone di sala)  → CodiceInstallatore
 *     POST /sala/abbina         ← AbbinaSala        → SessioneSala      (pubblica, il codice e' la chiave)
 *     POST /sala/rinnova        (gettone di sala)  → SessioneSala      (stessa sessione, scadenza nuova)
 */
export const APP_SALE = {
    /** Dove il server pubblica installatori e aggiornamenti (lo legge anche `electron-updater`). */
    cartella: '/sale/aggiornamenti/',
    windows: 'Beste-Sale-Windows.exe',
    mac: 'Beste-Sale-Mac.dmg',
    /** Il nome con cui si salva l'installatore Windows: il codice dentro il nome lo legge l'installatore. */
    nomeWindows: (codice) => `Beste-Sale-Windows-${codice}.exe`,
    /** Dieci caratteri, niente 0/O/1/I/L: si leggono a voce. */
    codice: /^[A-HJKMNP-Z2-9]{10}$/,
    protocollo: 'bestesale',
    /** Il codice muore se nessuno lo usa; la sessione che apre no. */
    giorniCodice: 7,
    /** Il gettone della sala si rinnova quando ha piu' di tanti giorni: cosi' non scade mai. */
    giorniRinnovo: 30,
};
/*
 * Gli account delle sale, **uno per sede**, dalla gestione delle sale (l'ufficio, con lo stesso
 * permesso che gestisce le sale). Prima stesura: uno per saletta; cambiato lo stesso giorno per
 * ordine di Cosimo (*«FAMMI UN ACCOUNT PER HUB, UNO PER COLLE E UNO PER CILIANI»*), con il
 * multilogin: lo stesso account aperto su piu' PC e in piu' finestre insieme.
 *
 *   GET    /api/account-sala                     → AccountSalaVista[] una riga per sala attiva
 *   PUT    /api/account-sala/:salaId             ← ImpostaAccountSala → AccountSalaVista
 *   DELETE /api/account-sala/:salaId             → AccountSalaVista   lo spegne, non lo cancella
 */
export const ACCOUNT_SALA = {
    /** Si scrive sul PC della saletta: minuscole, cifre, punto, trattino. */
    identificativo: { min: 3, max: 60, forma: /^[a-z0-9][a-z0-9._-]*$/ },
    /**
     * La parola si **sceglie** qui (la sceglie l'ufficio), quindi ha un minimo vero: non e' la
     * parola presentata del login, che si confronta e basta.
     */
    password: { min: 10, max: 200 },
};
/* ------------------------------------------------------------------ la chiamata diretta */
/**
 * La chiamata diretta che **squilla** (Cosimo, 2026-10-28, fra i miglioramenti scelti: «"Chiama"
 * da Rubrica e Chat: il telefono del collega SQUILLA come una telefonata vera»). Chi chiama crea
 * una chiamata riservata a due e uno **squillo**: il telefono del collega suona, il Cruscotto
 * aperto mostra la chiamata in arrivo, e chi chiama vede lo stato finche' qualcuno decide.
 *
 * Gli stati di uno squillo:
 * - `squilla`: appena partito, finche' nessuno decide e la durata non e' passata;
 * - `risposta`: il chiamato ha risposto — si entra tutt'e due nella stanza;
 * - `rifiutata`: il chiamato ha detto no;
 * - `annullata`: chi chiamava ha riattaccato prima;
 * - `persa`: nessuno ha deciso entro la durata. Al chiamato resta una notifica «chiamata persa».
 */
export const STATI_SQUILLO = ['squilla', 'risposta', 'rifiutata', 'annullata', 'persa'];
export const SQUILLO = {
    /**
     * Quanto squilla. 45 secondi e' quel che fa un telefono prima della segreteria: abbastanza per
     * tirarlo fuori dalla tasca, non tanto da far aspettare chi chiama davanti a uno schermo.
     */
    durataMs: 45_000,
    /** Ogni quanto il Cruscotto chiede se c'e' una chiamata in arrivo, a scheda visibile. */
    ascoltoMs: 3_000,
    /** Ogni quanto chi chiama rilegge lo stato del suo squillo. */
    attesaMs: 1_500,
};
//# sourceMappingURL=videochiamate.js.map